import { Ban, CheckCircle2, Search, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import type { ApiFraudAlert } from "@/api/perkpoint/fraud";
import {
  ALERT_TYPE_LABELS,
  OUTCOME_LABELS,
  SEVERITY_LABELS,
  SEVERITY_STYLES,
  STATUS_LABELS,
  STATUS_STYLES,
  actionLabel,
  formatDateTime,
  formatMinor,
  riskTone,
  type AlertStatus,
} from "./fraud-data";

interface AlertDetailSheetProps {
  alert: ApiFraudAlert | null;
  currency: string;
  currencyExponent: number;
  /** True while a status change or block is saving. */
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onStatusChange: (alert: ApiFraudAlert, status: AlertStatus) => void;
  onBlockMember: (alert: ApiFraudAlert) => void;
}

function Detail({ label, value, mono }: { label: string; value?: string | number | null; mono?: boolean }) {
  if (value === undefined || value === null || value === "") return null;
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={cn("mt-0.5 break-all text-sm text-slate-900", mono && "font-mono text-xs")}>{value}</dd>
    </div>
  );
}

export function AlertDetailSheet({
  alert,
  currency,
  currencyExponent,
  busy,
  onOpenChange,
  onStatusChange,
  onBlockMember,
}: AlertDetailSheetProps) {
  const isOpen = alert?.status === "active" || alert?.status === "investigating";
  const memberBlocked = alert?.member.status === "blocked";

  return (
    <Sheet open={!!alert} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        {alert && (
          <div className="space-y-6">
            <SheetHeader className="space-y-2 text-left">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset",
                    SEVERITY_STYLES[alert.severity],
                  )}
                >
                  {SEVERITY_LABELS[alert.severity]}
                </span>
                <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", STATUS_STYLES[alert.status])}>
                  {STATUS_LABELS[alert.status]}
                </span>
                <span className="text-xs text-muted-foreground">{alert.code}</span>
              </div>
              <SheetTitle className="text-xl">{alert.description}</SheetTitle>
              <SheetDescription>
                {alert.member.name} · {alert.member.external_member_id}
                {memberBlocked && <span className="ml-1 font-medium text-red-600">· Blocked</span>}
              </SheetDescription>
            </SheetHeader>

            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-end justify-between">
                <p className="text-sm text-muted-foreground">Risk score</p>
                <p className="text-3xl font-bold tabular-nums text-slate-900">
                  {alert.risk_score}
                  <span className="text-base font-normal text-muted-foreground">/100</span>
                </p>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className={cn("h-full rounded-full", riskTone(alert.risk_score))} style={{ width: `${alert.risk_score}%` }} />
              </div>
              <p className="mt-3 text-sm text-slate-700">{OUTCOME_LABELS[alert.outcome]}</p>
            </div>

            <dl className="grid grid-cols-2 gap-4">
              <Detail label="First detected" value={formatDateTime(alert.created_at)} />
              <Detail
                label="Times hit"
                value={alert.hit_count > 1 ? `${alert.hit_count} (last ${formatDateTime(alert.last_hit_at)})` : undefined}
              />
              <Detail label="Signal" value={ALERT_TYPE_LABELS[alert.alert_type]} />
              <Detail label="Matched rule" value={alert.rule ? `${alert.rule.code} · ${alert.rule.name}` : "Auto-block"} />
              <Detail label="Rule action" value={actionLabel(alert.action)} />
              <Detail label="Transaction" value={alert.transaction_kind === "burn" ? "Redemption" : "Earn"} />
              <Detail label="Outlet" value={alert.outlet} />
              <Detail
                label="Invoice amount"
                value={alert.amount_minor != null ? formatMinor(alert.amount_minor, currency, currencyExponent) : undefined}
              />
              <Detail label="Points redeemed" value={alert.points != null ? alert.points.toLocaleString("en-US") : undefined} />
              <Detail label="Reference" value={alert.reference_id} mono />
              <Detail label="Ledger entry" value={alert.txn_code} mono />
            </dl>
            {alert.resolution_note && (
              <dl>
                <Detail label="Review note" value={alert.resolution_note} />
              </dl>
            )}

            <div className="space-y-2 border-t border-slate-200 pt-4">
              <p className="text-sm font-medium text-slate-900">Take action</p>
              {isOpen ? (
                <div className="grid grid-cols-2 gap-2">
                  {alert.status === "active" && (
                    <Button
                      variant="outline"
                      className="col-span-2"
                      disabled={busy}
                      onClick={() => onStatusChange(alert, "investigating")}
                    >
                      <Search className="mr-2 h-4 w-4" /> Start investigating
                    </Button>
                  )}
                  <Button variant="outline" disabled={busy} onClick={() => onStatusChange(alert, "resolved")}>
                    <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" /> Resolve
                  </Button>
                  <Button variant="outline" disabled={busy} onClick={() => onStatusChange(alert, "false_positive")}>
                    <XCircle className="mr-2 h-4 w-4 text-slate-500" /> False positive
                  </Button>
                  {!memberBlocked && (
                    <Button
                      variant="destructive"
                      className="col-span-2"
                      disabled={busy}
                      onClick={() => onBlockMember(alert)}
                    >
                      <Ban className="mr-2 h-4 w-4" /> Block member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3 rounded-md bg-slate-50 p-3">
                  <p className="text-sm text-muted-foreground">This alert is closed.</p>
                  <Button variant="outline" size="sm" disabled={busy} onClick={() => onStatusChange(alert, "active")}>
                    Reopen
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Changing the status does not undo or re-post the transaction.
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
